export type TrxRefreshResult = 'updated' | 'failed' | 'busy';
type Reader = () => Promise<TrxRefreshResult>;

// Reuse the mounted live view's reader and its request guards.
const reader = shallowRef<Reader>();
const refreshing = ref(false);
const result = ref<TrxRefreshResult>();
let generation = 0;

export function useTrxLiveRefresh() {
  function register(read: Reader) {
    reader.value = read;
    ++generation;
    refreshing.value = false;
    result.value = undefined;
    onScopeDispose(() => {
      if (reader.value === read) {
        reader.value = undefined;
        ++generation;
        refreshing.value = false;
        result.value = undefined;
      }
    });
  }
  async function refresh() {
    const read = reader.value;
    if (!read || refreshing.value) return;
    const request = generation;
    refreshing.value = true;
    try {
      const next = await read();
      if (reader.value === read && generation === request) result.value = next;
    } catch {
      if (reader.value === read && generation === request) result.value = 'failed';
    } finally {
      if (generation === request) refreshing.value = false;
    }
  }
  return {
    available: computed(() => !!reader.value),
    refreshing: readonly(refreshing),
    result: readonly(result),
    register,
    refresh,
  };
}
